/**
 * Live TV Currently On Air – client update without page reload (ADR-023).
 * - PubNub (primary): subscribe to tjc-auctionended and tjc-livetv; on auction end show empty, on new auction refresh card
 *   after NEW_AUCTION_REFRESH_DELAY_MS (the message arrives before the metafield the page renders from is written).
 * - Polling (fallback): GET /apps/live-tv/currently-on-air every 10s; same DOM updates when isLive/product changes.
 * - Card refresh: fetch full page HTML and replace card column with .live-tv-page__card-column from response.
 *   Concurrent fetches are aborted + ignored (epoch) so auction end cannot be overwritten by a stale livetv response.
 * - After inject: re-run initCoaCardForms + initViewDetailsPopups on the slot (see live-tv-card-forms.js).
 * - Auction identity is {auctionCode, sku}, normalised (trim + lower-case) and read by the same extractor for PubNub
 *   and poll. Both sources compare against the identity stamped on the rendered card, never against each other, so
 *   they cannot disagree about "which auction is on screen" and ping-pong full refreshes between them. Two identities
 *   contradict only where a field both carry disagrees: a payload missing a field proves nothing and counts as a tick
 *   for the auction already on screen, and one carrying only a sku still detects a lot change.
 * - Selection preservation: the shopper's GLA/MLA/variant choice, quantity and Budget Pay toggle are snapshotted before
 *   every HTML swap and replayed afterwards when the refreshed card is still the same auction (a genuinely new auction
 *   must start clean). Options are matched by sku/auction code, not by index or variant id — ids are re-resolved per
 *   render. Same-auction refreshes are also skipped entirely while the shopper is mid-interaction.
 * - PubNub payload may include currentPrice (camelCase or MainAuctions.CurrentPrice); we patch the card DOM after
 *   HTML swap so displayed price matches the event when Shopify HTML is briefly stale.
 * - Budget Pay: stash budgetPay from PubNub/poll; after HTML swap apply visibility to [data-ltv-budget-pay]; if PubNub
 *   enables BP before metafield HTML includes the slot, debounce refresh + poll API until aligned (avoid hand-built plans JSON).
 * - Set localStorage ltv_coa_debug=1 for a console trace of every refresh and its trigger.
 */
(function() {
  'use strict';

  var POLL_INTERVAL_MS = 10000;
  var POLL_MAX_BACKOFF_TICKS = 3;
  var SECTION_SLOT_SELECTOR = '[data-currently-on-air-slot]';
  var CARD_COLUMN_SELECTOR = '.live-tv-page__card-column';
  var COA_FORM_SELECTOR = '.currently-on-air-card__form';
  var GLA_SELECT_SELECTOR = '[data-gla-select]';
  var MLA_SELECT_SELECTOR = '[data-mla-select]';
  var VARIANT_SELECT_SELECTOR = '[data-variant-select]';
  var QTY_INPUT_SELECTOR = '.currently-on-air-card__qty-input';
  var BUDGET_PAY_TOGGLE_SELECTOR = '[data-budget-pay-toggle]';
  /* Card and popup render the same figures under their own class names; both must be patched. */
  var PRICE_SELECTOR = '.currently-on-air-card__price, .view-details-popup__price';
  var INSTALLMENTS_AMOUNT_SELECTOR =
    '.currently-on-air-card__installments-amount, .view-details-popup__installments-amount';
  /* Marks an <option> whose data-line-price a live tick has actually priced, as opposed to the
     page-load figure the server stamped. Deliberately not persisted: a card swap re-renders the
     options from the server, and those values must earn the marker again. */
  var LINE_PRICE_LIVE_ATTR = 'data-line-price-live';
  /* MLA options are built by the GLA change handler, whose listener can attach only after a config
     fetch resolves — retry the replay for ~1s before giving up. */
  var SELECTION_RESTORE_ATTEMPTS = 6;
  var SELECTION_RESTORE_DELAY_MS = 150;
  /* The API's auth-fallback branch answers isLive:false when Shopify auth is momentarily unavailable,
     which is indistinguishable from a real auction end — require two in a row before clearing the card. */
  var NOT_LIVE_POLLS_BEFORE_EMPTY = 2;
  var BUDGET_PAY_RECONCILE_MAX_ATTEMPTS = 2;
  /* Shorter than POLL_INTERVAL_MS so a lagging page render still gets retried every poll, while a
     burst of PubNub bids for the same new auction collapses into one page fetch. */
  var REFRESH_RETARGET_COOLDOWN_MS = 8000;
  /* ~30s of retries is enough for the metafield write and page render to catch up. Past that the
     auction is probably unrenderable (e.g. its product is unpublished), so back off instead of
     re-fetching the page every poll for the rest of the show. */
  var MAX_REFRESHES_PER_TARGET = 3;
  var REFRESH_TARGET_RETRY_AFTER_MS = 60000;
  /* AMS publishes a new auction to PubNub and Kafka at the same moment, and the Kafka consumer needs
     ~1-3 s to write the shop metafield the page renders from. A refresh fired on the PubNub message
     therefore rendered the previous lot, started the cooldown above, and the new lot waited for a
     later tick or poll (up to ~20 s). Wait about as long as that write takes instead; the random
     part spreads every viewer's page fetch over a second rather than one burst. */
  var NEW_AUCTION_REFRESH_DELAY_MS = 2500;
  var NEW_AUCTION_REFRESH_JITTER_MS = 1000;
  /* If a refresh still renders a different lot (metafield or page render running late), retry once
     on a timer rather than waiting for the next trigger. It counts toward MAX_REFRESHES_PER_TARGET,
     so the most page fetches per lot per viewer stays where it was. */
  var NEW_AUCTION_RETRY_DELAY_MS = 3000;
  /* A cooldown-exempt request (poll / timed retry) for a lot whose page fetch started this recently is
     dropped: that fetch is still loading or has just landed, and restarting it only wastes a request. */
  var REFRESH_SAME_TARGET_MIN_GAP_MS = 2000;
  /* PubNub announces an auction end before the end consumer clears the metafield, and the app serves
     the metafield from a 3 s cache, so for a few seconds a poll still reports the ended lot as live
     and put it back on the emptied card. Polls, late PubNub ticks and card refreshes naming the lot
     PubNub just ended are ignored for this long (isEndedAuction); past it they are trusted again
     (e.g. the clear failed). */
  var ENDED_LOT_POLL_IGNORE_MS = 15000;
  /* The metafield trails a PubNub price tick by the consumer write (~1-2 s) plus that 3 s cache, so a
     poll in that window carried the previous price and stamped it over the newer tick — the price
     jumped back up until the next tick. The poll leaves the main price alone this long after a tick;
     line prices, Budget Pay and the sold count from the poll are unaffected. */
  var PUBNUB_PRICE_FRESH_MS = 6000;
  var LTV_COA_DEBUG_KEY = 'ltv_coa_debug';
  /* LTV_AUCTION_JOURNEY (TWS-1497), QA only: with ?ltv_trace=1 (remembered in this browser;
     ?ltv_trace=0 turns it off) the browser times each new auction from the first PubNub message or
     poll that names it until it is drawn on the card, and reports that once to
     /apps/live-tv/journey, which logs it for Kibana next to the backend's steps. Off by default:
     no request, no work. */
  var LTV_TRACE_KEY = 'ltv_trace';
  var JOURNEY_KEYWORD = 'LTV_AUCTION_JOURNEY';
  /* A traced lot still not on the card this long after the browser first heard of it reports
     NOT_DISPLAYED (and DISPLAYED_ON_SCREEN later if it does appear), so slow lots always leave a line. */
  var JOURNEY_NOT_DISPLAYED_MS = 30000;

  function isJourneyTraceEnabled() {
    try {
      var m = /[?&]ltv_trace=([01])(?:&|$)/.exec(window.location.search);
      if (m) {
        if (m[1] === '1') window.localStorage.setItem(LTV_TRACE_KEY, '1');
        else window.localStorage.removeItem(LTV_TRACE_KEY);
      }
      return window.localStorage.getItem(LTV_TRACE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function isCoaDebugEnabled() {
    try {
      return typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem(LTV_COA_DEBUG_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function coaDebugLog(label, detail) {
    if (!isCoaDebugEnabled()) return;
    if (detail !== undefined) console.log('[LTV][COA] ' + label, detail);
    else console.log('[LTV][COA] ' + label);
  }

  /** Auction codes arrive from AMS, Kafka and Liquid — compare them case/whitespace-insensitively. */
  function normCode(v) {
    return v == null ? '' : String(v).trim().toLowerCase();
  }

  function formatMoney(amount, fmt) {
    if (amount == null || isNaN(Number(amount))) return '';
    var num = Number(amount);
    var str = num.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    return (fmt || '{{amount}}').replace(/\{\{amount\}\}/g, str).replace(/\{\{amount_no_decimals\}\}/g, Math.round(num).toString());
  }

  /**
   * The live price from any payload shape.
   *
   * Parsed, not type-checked. AMS puts CurrentPrice on the wire as a STRING ("51.99" — see the
   * captured envelope in livetv-updates-consumer/src/consumer.test.ts), so the old typeof check
   * answered null for every real message on the live channel. pendingPubNubPrice then kept whatever
   * the last numerically-priced message had left in it — another lot's price — and every Budget Pay
   * figure divided that instead. It is how a £149.99 lot came to advertise 5 payments of £913.60:
   * 4568 / 5, carried over from a different auction. readAuctionLine() below already parses its
   * rows the same way, which is why the per-line prices were right while the main one was not.
   * @returns {number|null} null when the payload carries no usable price
   */
  function extractLivePrice(msg) {
    if (msg == null) return null;
    var main = msg.MainAuctions || msg.mainAuctions || {};
    var candidates = [msg.currentPrice, main.CurrentPrice, main.currentPrice];
    for (var i = 0; i < candidates.length; i++) {
      var raw = candidates[i];
      if (raw == null || raw === '') continue;
      var n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  }

  /**
   * Live StockRemaining from a PubNub tick (MainAuctions.StockRemaining in the AMS payload).
   * @returns {number|null} null when the payload carries no usable stock
   */
  function extractStockRemaining(msg) {
    if (msg == null) return null;
    var main = msg.MainAuctions || msg.mainAuctions || {};
    var candidates = [msg.stockRemaining, main.StockRemaining, main.stockRemaining];
    for (var i = 0; i < candidates.length; i++) {
      var raw = candidates[i];
      if (raw == null || raw === '') continue;
      var n = Number(raw);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    return null;
  }

  /**
   * "Over X items sold" badge for the auction on screen. sold = data-initial-stock − live stock, or the
   * poll's soldSoFar; never lower than what is shown (ticks repeat and arrive out of order). A new
   * auction re-renders the card from the server, which is what resets the count to its own 0.
   */
  function applySoldCount(targetSlot, stockRemaining, soldSoFar) {
    var badge = targetSlot && targetSlot.querySelector('[data-coa-sold-badge]');
    var countEl = badge && badge.querySelector('[data-coa-sold-count]');
    if (!countEl) return;
    var sold = parseInt(countEl.textContent, 10) || 0;
    var initialAttr = (badge.getAttribute('data-initial-stock') || '').trim();
    var initial = Number(initialAttr);
    if (stockRemaining != null && initialAttr !== '' && Number.isFinite(initial)) {
      sold = Math.max(sold, initial - stockRemaining);
    }
    if (soldSoFar != null && Number.isFinite(soldSoFar)) sold = Math.max(sold, soldSoFar);
    countEl.textContent = String(sold);
    var min = Number(badge.getAttribute('data-sold-min')) || 0;
    badge.hidden = !(sold > 0 && sold >= min);
  }

  /**
   * The auction identity, normalised. Used for BOTH PubNub messages and the poll payload so the two
   * can never resolve the same auction to different strings. Deliberately does not fall back to a
   * SKU: a stock code is not an auction code, and treating it as one made each source overwrite the
   * other's idea of the current auction, refreshing the card (and wiping the selection) on every tick.
   * @returns {string} '' when the payload carries no auction identity
   */
  function extractPackageCode(msg) {
    if (msg == null) return '';
    if (normCode(msg.packageCode) !== '') return normCode(msg.packageCode);
    if (normCode(msg.auctionCode) !== '') return normCode(msg.auctionCode);
    var main = msg.MainAuctions || msg.mainAuctions;
    if (main) {
      var ac = main.Auctioncode != null ? main.Auctioncode : (main.AuctionCode != null ? main.AuctionCode : main.auctionCode);
      if (normCode(ac) !== '') return normCode(ac);
      if (normCode(main.PackageCode) !== '') return normCode(main.PackageCode);
      if (normCode(main.packageCode) !== '') return normCode(main.packageCode);
    }
    return '';
  }

  /**
   * @returns {{ enabled: boolean, emiCount: number, emiPrice: number | null } | null} null = no Budget Pay signal in message
   */
  function extractBudgetPayState(msg, currentPrice) {
    if (msg == null) return null;
    var bp = msg.budgetPay || msg.BudgetPay;
    var main = msg.MainAuctions || msg.mainAuctions || {};
    var offerBp = main.OfferBudgetPay === true;
    var hasBp = bp && typeof bp === 'object';
    if (!hasBp && !offerBp) return null;
    var enabled =
      offerBp ||
      (hasBp &&
        (bp.enabled === true ||
          bp.Enabled === true ||
          bp.isBPAuction === true ||
          bp.IsBPAuction === true));
    var emiCount =
      Number(
        (hasBp && (bp.emiCount != null ? bp.emiCount : bp.EmiCount)) || main.NumberOfEMI || 0
      ) || 0;
    /* Always price / emiCount. The payload's own installment (bp.emiPrice, or AMS's
       MainAuctions.BPInstallmentAmount) is computed once at auction start and is not re-sent on bid
       ticks, so reading it froze the Budget Pay figure on prod while the main price kept moving.
       The dev simulator recomputes it on every tick, which is why only prod showed the stale value.
       null when the price is unknown: the caller then leaves the rendered figure alone rather than
       stamping a number derived from nothing. */
    var emiPrice = null;
    var n = Number(emiCount);
    if (n >= 1 && currentPrice != null && Number.isFinite(Number(currentPrice))) {
      emiPrice = Number(currentPrice) / n;
    }
    return { enabled: !!enabled, emiCount: emiCount, emiPrice: emiPrice };
  }

  function extractBudgetPayInstallment(msg, currentPrice) {
    var st = extractBudgetPayState(msg, currentPrice);
    if (!st || !st.enabled) return null;
    return st.emiPrice;
  }

  /**
   * The auction's stock code. Tracked alongside the auction code because not every payload carries
   * a usable auction code, and a lot change always changes the sku — relying on the auction code
   * alone means a new auction can go undetected and the card never updates without a page reload.
   * @returns {string} '' when the payload carries no sku
   */
  function extractSku(msg) {
    if (msg == null) return '';
    var direct = [msg.sku, msg.stockCode, msg.StockCode, msg.productID, msg.ProductID];
    for (var i = 0; i < direct.length; i++) {
      if (normCode(direct[i]) !== '') return normCode(direct[i]);
    }
    var main = msg.MainAuctions || msg.mainAuctions;
    if (main) {
      var nested = [main.StockCode, main.stockCode, main.ProductID, main.productID, main.Sku, main.sku];
      for (var j = 0; j < nested.length; j++) {
        if (normCode(nested[j]) !== '') return normCode(nested[j]);
      }
    }
    return '';
  }

  function messageIdentity(msg) {
    return { code: extractPackageCode(msg), sku: extractSku(msg) };
  }

  /**
   * Two identities contradict only when a field both of them carry disagrees. A missing field
   * proves nothing, so a partial payload is never mistaken for a different auction.
   */
  function identitiesDiffer(a, b) {
    if (!a || !b) return false;
    if (a.code !== '' && b.code !== '' && a.code !== b.code) return true;
    if (a.sku !== '' && b.sku !== '' && a.sku !== b.sku) return true;
    return false;
  }

  function identityKey(id) {
    return id ? id.code + '|' + id.sku : '|';
  }

  function hasIdentity(id) {
    return !!id && (id.code !== '' || id.sku !== '');
  }

  /**
   * Record the rendered card's identity on the slot. Both the hidden _auctionCode input and the
   * displayed sku are rewritten as soon as the shopper picks a GLA/MLA row, so neither can be read
   * as the card's identity after an interaction — stamp them while the markup is still untouched.
   * The stamped values are the ground truth every inbound message is compared against, which is
   * also what stops PubNub and the poll from disagreeing with each other and refreshing in a loop.
   */
  function stampCardIdentity(targetSlot) {
    if (!targetSlot) return { code: '', sku: '' };
    var inp =
      targetSlot.querySelector('input[name="properties[_auctionCode]"]') ||
      targetSlot.querySelector('input[name="properties[auctionCode]"]');
    var skuEl = targetSlot.querySelector('.currently-on-air-card__sku');
    var id = {
      code: normCode(inp && inp.value),
      sku: normCode(skuEl && skuEl.textContent)
    };
    if (id.code) targetSlot.setAttribute('data-ltv-card-auction-code', id.code);
    else targetSlot.removeAttribute('data-ltv-card-auction-code');
    if (id.sku) targetSlot.setAttribute('data-ltv-card-sku', id.sku);
    else targetSlot.removeAttribute('data-ltv-card-sku');
    return id;
  }

  function readCardIdentity(targetSlot) {
    if (!targetSlot) return { code: '', sku: '' };
    return {
      code: normCode(targetSlot.getAttribute('data-ltv-card-auction-code')),
      sku: normCode(targetSlot.getAttribute('data-ltv-card-sku'))
    };
  }

  function clearCardIdentity(targetSlot) {
    if (!targetSlot) return;
    targetSlot.removeAttribute('data-ltv-card-auction-code');
    targetSlot.removeAttribute('data-ltv-card-sku');
  }

  /**
   * Identity of the option a shopper picked, robust across re-renders. Variant ids are re-resolved
   * on every render (and sometimes only after an async /sku-data lookup), so they are the weakest
   * key; sku and auction code are stable.
   * @returns {{value: string, sku: string, code: string, text: string} | null} null for the placeholder / no selection
   */
  function selectedOptionKey(select) {
    if (!select || !select.options || select.selectedIndex < 0) return null;
    var opt = select.options[select.selectedIndex];
    if (!opt) return null;
    var key = {
      value: String(opt.value == null ? '' : opt.value).trim(),
      sku: normCode(opt.getAttribute('data-sku')),
      code: normCode(opt.getAttribute('data-auction-code')),
      text: normCode(opt.textContent)
    };
    if (!key.value && !key.sku && !key.code) return null;
    return key;
  }

  function findOptionForKey(select, key) {
    if (!select || !select.options || !key) return null;
    var opts = select.options;
    var i;
    for (i = 0; i < opts.length; i++) {
      if (key.sku && normCode(opts[i].getAttribute('data-sku')) === key.sku) return opts[i];
    }
    for (i = 0; i < opts.length; i++) {
      if (key.code && normCode(opts[i].getAttribute('data-auction-code')) === key.code) return opts[i];
    }
    for (i = 0; i < opts.length; i++) {
      if (key.value && String(opts[i].value || '').trim() === key.value) return opts[i];
    }
    /* Last resort for options that carry neither sku nor auction code of their own (ring-resize
       sizes, Buy All). Fails when a "(sold out)" suffix has since been appended, which is fine —
       that option should not be restored anyway. */
    for (i = 0; i < opts.length; i++) {
      if (key.text && normCode(opts[i].textContent) === key.text) return opts[i];
    }
    return null;
  }

  /* Replay via a dispatched change event rather than any module's internals: GLA/MLA is owned by
     app.js's initCardForm when app.js is loaded and by live-tv-card-forms.js otherwise, and both
     listen for change. */
  function selectOptionAndNotify(select, opt) {
    if (!select || !opt || opt.disabled) return false;
    opt.selected = true;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  /** @returns {object | null} null when the shopper has not touched the card (nothing to restore) */
  function snapshotCardSelection(targetSlot) {
    if (!targetSlot) return null;
    var form = targetSlot.querySelector(COA_FORM_SELECTOR);
    if (!form) return null;
    var qty = form.querySelector(QTY_INPUT_SELECTOR);
    var toggle = targetSlot.querySelector(BUDGET_PAY_TOGGLE_SELECTOR);
    var snap = {
      identity: identityKey(readCardIdentity(targetSlot)),
      gla: selectedOptionKey(form.querySelector(GLA_SELECT_SELECTOR)),
      mla: selectedOptionKey(form.querySelector(MLA_SELECT_SELECTOR)),
      variant: selectedOptionKey(form.querySelector(VARIANT_SELECT_SELECTOR)),
      qty: qty ? String(qty.value == null ? '' : qty.value).trim() : '',
      budgetPayActive: !!(toggle && toggle.hasAttribute('data-active'))
    };
    var qtyChanged = snap.qty !== '' && snap.qty !== '1';
    if (!snap.gla && !snap.mla && !snap.variant && !snap.budgetPayActive && !qtyChanged) return null;
    return snap;
  }

  function restoreQuantity(form, snap) {
    if (!form || !snap || snap.qty === '') return;
    var qty = form.querySelector(QTY_INPUT_SELECTOR);
    if (!qty || String(qty.value == null ? '' : qty.value).trim() === snap.qty) return;
    var max = qty.hasAttribute('max') ? parseInt(qty.getAttribute('max'), 10) : NaN;
    var n = parseInt(snap.qty, 10);
    if (isNaN(n) || n < 1) return;
    if (!isNaN(max) && n > max) n = max;
    qty.value = String(n);
    qty.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function restoreBudgetPayToggle(targetSlot, snap) {
    if (!snap || !snap.budgetPayActive) return true;
    var toggle = targetSlot.querySelector(BUDGET_PAY_TOGGLE_SELECTOR);
    if (!toggle) return false;
    if (toggle.hasAttribute('data-active')) return true;
    var inst = toggle._budgetPayToggleInstance;
    /* Without a resolved selling plan the toggle handler only warns and no-ops; wait for the
       instance instead of clicking into a console warning on every retry. */
    if (!inst || !inst.sellingPlanGid) return false;
    var btn = toggle.querySelector('button');
    if (!btn || btn.disabled) return false;
    btn.click();
    return true;
  }

  /**
   * Re-apply a snapshot to a freshly injected card. GLA goes first — its change handler rebuilds the
   * MLA options — and the whole pass retries while anything it needs is still missing.
   * @param {function(): boolean} isStale — the retry chain outlives its card when another refresh
   *   lands mid-replay; without this the snapshot would be applied to the next auction's markup.
   */
  function restoreCardSelection(targetSlot, snap, attempt, isStale) {
    if (isStale && isStale()) return;
    var form = targetSlot && targetSlot.querySelector(COA_FORM_SELECTOR);
    if (!form || !snap) return;
    var n = attempt || 0;
    var needMoreTime = false;

    if (n === 0) restoreQuantity(form, snap);

    var variantSelect = form.querySelector(VARIANT_SELECT_SELECTOR);
    if (variantSelect && snap.variant) {
      var vOpt = findOptionForKey(variantSelect, snap.variant);
      if (!vOpt) needMoreTime = true;
      else if (!vOpt.selected) selectOptionAndNotify(variantSelect, vOpt);
    }

    var glaSelect = form.querySelector(GLA_SELECT_SELECTOR);
    var mlaSelect = form.querySelector(MLA_SELECT_SELECTOR);
    if (glaSelect && snap.gla) {
      var glaOpt = findOptionForKey(glaSelect, snap.gla);
      if (!glaOpt) {
        needMoreTime = true;
      } else {
        /* Re-dispatch while the MLA list is still empty: the GLA listener may not have been
           attached when the first dispatch ran, so nothing rebuilt the child options. */
        if (!glaOpt.selected || (mlaSelect && mlaSelect.options.length <= 1)) {
          selectOptionAndNotify(glaSelect, glaOpt);
        }
        if (snap.mla && mlaSelect) {
          var mlaOpt = findOptionForKey(mlaSelect, snap.mla);
          if (!mlaOpt) needMoreTime = true;
          else if (!mlaOpt.selected) selectOptionAndNotify(mlaSelect, mlaOpt);
        }
      }
    }

    if (!restoreBudgetPayToggle(targetSlot, snap)) needMoreTime = true;

    if (needMoreTime && n < SELECTION_RESTORE_ATTEMPTS) {
      setTimeout(function() {
        restoreCardSelection(targetSlot, snap, n + 1, isStale);
      }, SELECTION_RESTORE_DELAY_MS);
    } else {
      coaDebugLog('restoreCardSelection done', { attempts: n, incomplete: needMoreTime, snapshot: snap });
    }
  }

  /** True while the shopper is mid-interaction — a same-auction refresh must not yank the card away. */
  function cardHasShopperInput(targetSlot) {
    if (!targetSlot) return false;
    var form = targetSlot.querySelector(COA_FORM_SELECTOR);
    if (!form) return false;
    if (document.activeElement && targetSlot.contains(document.activeElement)) return true;
    if (document.querySelector('.view-details-popup.is-open')) return true;
    var sels = form.querySelectorAll(
      GLA_SELECT_SELECTOR + ',' + MLA_SELECT_SELECTOR + ',' + VARIANT_SELECT_SELECTOR
    );
    for (var i = 0; i < sels.length; i++) {
      if (selectedOptionKey(sels[i])) return true;
    }
    var toggle = targetSlot.querySelector(BUDGET_PAY_TOGGLE_SELECTOR);
    if (toggle && toggle.hasAttribute('data-active')) return true;
    var qty = form.querySelector(QTY_INPUT_SELECTOR);
    var qtyVal = qty ? String(qty.value == null ? '' : qty.value).trim() : '';
    if (qtyVal !== '' && qtyVal !== '1') return true;
    return false;
  }

  function findBlockRoots() {
    var list = document.querySelectorAll('[data-currently-on-air-api-url]');
    var roots = [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].querySelector(SECTION_SLOT_SELECTOR)) roots.push(list[i]);
    }
    return roots;
  }

  function run() {
    var roots = findBlockRoots();
    for (var r = 0; r < roots.length; r++) initBlock(roots[r]);
  }

  function initBlock(blockRoot) {

    var slot = blockRoot.querySelector(SECTION_SLOT_SELECTOR);
    var emptyTemplate = blockRoot.querySelector('[data-currently-on-air-empty]');
    var sectionId = slot && slot.getAttribute('data-section-id-for-refresh');
    var apiUrl = blockRoot.getAttribute('data-currently-on-air-api-url') || '/apps/live-tv/currently-on-air';
    var subscribeKey = (blockRoot.getAttribute('data-pubnub-subscribe-key') || '').trim();
    var channelLivetv = (blockRoot.getAttribute('data-pubnub-channel-livetv') || 'iw-livetv').trim();
    var channelAuctionEnded = (blockRoot.getAttribute('data-pubnub-channel-auction-ended') || 'iw-auctionended').trim();
    var moneyFormat = blockRoot.getAttribute('data-money-format') || '{{amount}}';

    if (!slot || !emptyTemplate || !sectionId) return;

    var existingIdentity = stampCardIdentity(slot);
    var lastIsLive = hasIdentity(existingIdentity) ? true : null;
    var lastPrice = null;
    var notLivePolls = 0;
    var cardUiEpoch = 0;
    var cardFetchAbort = null;
    var pendingPubNubPrice = null;
    var pendingIdentity = { code: '', sku: '' };
    var pendingPubNubEmi = null;
    var pendingBudgetPay = null;
    /* The auction pendingBudgetPay describes. It is NOT pendingIdentity: that one tracks the most
       recent message of any kind and moves on as soon as a tick for the card arrives, while
       pendingBudgetPay is only rewritten when a payload actually carries Budget Pay. A message for
       another lot set both; the next tick for the card moved pendingIdentity back, and the stale
       installment then passed the guard and was stamped on screen — a £379.99 lot advertising
       6 x £800.00 (4800 / 6). Bound here so the figure can only ever land on its own auction. */
    var pendingBudgetPayIdentity = null;
    var budgetPayReconcileTimer = null;
    var budgetPayReconcileCode = '';
    var budgetPayReconcileAttempts = 0;
    /* The Shopify page HTML can lag the API by a beat on an auction change; without a cooldown a
       burst of PubNub messages would each re-fetch it while it still renders the previous lot. */
    var lastRefreshTarget = '';
    var lastRefreshAt = 0;
    var sameTargetRefreshes = 0;
    /* The one delayed refresh a new lot's first PubNub message schedules (NEW_AUCTION_REFRESH_*). */
    var newAuctionRefreshTimer = null;
    var newAuctionRefreshKey = '';
    /* The one timed retry allowed per lot per attempt window when a refresh renders another lot. */
    var missRetryTimer = null;
    var missRetryUsedFor = '';
    /* The lot named by the last PubNub end message (ENDED_LOT_POLL_IGNORE_MS). */
    var endedLotIdentity = null;
    var endedLotAt = 0;

    /* TWS-1446 guard on TWS-1497's ended-lot state. The merge of develop into TWS-1497 dropped these
       two while keeping their callers, so every card swap and every tjc-livetv message threw a
       ReferenceError: the card stayed empty until a reload and PubNub never refreshed it. */
    function endedRecently() {
      return hasIdentity(endedLotIdentity) && Date.now() - endedLotAt < ENDED_LOT_POLL_IGNORE_MS;
    }

    function isEndedAuction(id) {
      return endedRecently() && hasIdentity(id) && !identitiesDiffer(id, endedLotIdentity);
    }
    /* When a PubNub tick last priced the card on screen (PUBNUB_PRICE_FRESH_MS). */
    var lastPubNubPriceAt = 0;
    /* LTV_AUCTION_JOURNEY tracing (QA only, see LTV_TRACE_KEY). */
    var traceOn = isJourneyTraceEnabled();
    var journeyUrl = /currently-on-air(?=$|\?)/.test(apiUrl)
      ? apiUrl.replace(/currently-on-air(?=$|\?)/, 'journey').split('?')[0]
      : '/apps/live-tv/journey';
    var journey = null;
    var journeyReportedKey = '';

    /* Codes exactly as AMS sent them (identity keys are lower-cased), in extractPackageCode() /
       extractSku() order, so the browser's lines match the backend's in Kibana. */
    function rawFirst(values) {
      for (var i = 0; i < values.length; i++) {
        if (values[i] != null && String(values[i]).trim() !== '') return String(values[i]).trim();
      }
      return '';
    }
    function rawCodeOf(src) {
      if (!src) return '';
      var main = src.MainAuctions || src.mainAuctions || {};
      return rawFirst([src.packageCode, src.auctionCode, main.Auctioncode, main.AuctionCode, main.auctionCode, main.PackageCode, main.packageCode]);
    }
    function rawSkuOf(src) {
      if (!src) return '';
      var main = src.MainAuctions || src.mainAuctions || {};
      return rawFirst([src.sku, src.stockCode, src.StockCode, src.productID, src.ProductID, main.StockCode, main.stockCode, main.ProductID, main.productID, main.Sku, main.sku]);
    }

    /**
     * A PubNub message or poll named a lot the card does not show: start (or note) its journey.
     * @param {Object} src — the message / poll data, for the exact codes.
     */
    function journeyNoteSeen(by, target, src) {
      if (!traceOn || !hasIdentity(target)) return;
      var key = identityKey(target);
      if (key === journeyReportedKey) return;
      var now = Date.now();
      if (!journey || journey.key !== key) {
        /* A newer lot arrived before the previous one was drawn: that one still gets a final line. */
        if (journey) journeyGiveUp(journey, 'replaced');
        var j = {
          key: key, target: target, rawCode: rawCodeOf(src), rawSku: rawSkuOf(src),
          firstSeenBy: by, firstSeenAt: now, attempts: 0
        };
        j.timer = setTimeout(function () {
          if (journey === j) journeyGiveUp(j, 'timeout');
        }, JOURNEY_NOT_DISPLAYED_MS);
        journey = j;
        console.log('[' + JOURNEY_KEYWORD + '] new lot seen via ' + by, { target: key, at: new Date(now).toISOString() });
      }
      if (by === 'poll' && !journey.pollSawAt) journey.pollSawAt = now;
    }

    /**
     * NOT_DISPLAYED, at most once per lot. After a timeout the journey stays open, so the lot still
     * reports DISPLAYED_ON_SCREEN if it appears later.
     */
    function journeyGiveUp(j, reason) {
      if (j.timer) {
        clearTimeout(j.timer);
        j.timer = null;
      }
      if (j.gaveUp) return;
      j.gaveUp = true;
      sendJourneyReport(j, 'not-displayed', reason, j.rawCode || j.target.code, j.rawSku || j.target.sku, null);
    }

    function journeyFor(target) {
      return traceOn && journey && target && journey.key === identityKey(target) ? journey : null;
    }

    function journeyNoteScheduled(target, delayMs) {
      var j = journeyFor(target);
      if (j && !j.scheduledAt) {
        j.scheduledAt = Date.now();
        j.scheduledDelayMs = delayMs;
      }
    }

    function journeyNoteFetch(target, trigger) {
      var j = journeyFor(target);
      if (!j) return;
      j.attempts++;
      j.trigger = trigger;
      j.fetchStartedAt = Date.now();
      j.fetchedAt = null;
    }

    function journeyNoteFetched(target) {
      var j = journeyFor(target);
      if (j && j.fetchStartedAt && !j.fetchedAt) j.fetchedAt = Date.now();
    }

    /** After every card swap: if the card now shows the traced lot, report it once and stop. */
    function journeyNoteDisplayed() {
      if (!traceOn || !journey || !cardShowsTarget(journey.target)) return;
      var j = journey;
      journey = null;
      journeyReportedKey = j.key;
      if (j.timer) {
        clearTimeout(j.timer);
        j.timer = null;
      }
      /* The card's own codes (untouched right after the swap) are the most exact. */
      var inp =
        slot.querySelector('input[name="properties[_auctionCode]"]') ||
        slot.querySelector('input[name="properties[auctionCode]"]');
      var skuEl = slot.querySelector('.currently-on-air-card__sku');
      sendJourneyReport(
        j, 'displayed', null,
        inp ? String(inp.value).trim() : (j.rawCode || j.target.code),
        skuEl ? String(skuEl.textContent).trim() : (j.rawSku || j.target.sku),
        Date.now()
      );
    }

    function sendJourneyReport(j, outcome, reason, code, sku, displayedAt) {
      var params = {
        outcome: outcome,
        reason: reason,
        code: code,
        sku: sku,
        firstSeenBy: j.firstSeenBy,
        firstSeenAt: j.firstSeenAt,
        scheduledAt: j.scheduledAt,
        scheduledDelayMs: j.scheduledDelayMs,
        pollSawAt: j.pollSawAt,
        fetchStartedAt: j.fetchStartedAt,
        fetchedAt: j.fetchedAt,
        displayedAt: displayedAt,
        trigger: j.trigger,
        attempts: j.attempts
      };
      var label = outcome === 'displayed' ? 'DISPLAYED_ON_SCREEN' : 'NOT_DISPLAYED (' + reason + ')';
      console.log('[' + JOURNEY_KEYWORD + '] ' + code + ' ' + label, {
        afterFirstSeenMs: Date.now() - j.firstSeenAt,
        details: params
      });
      params.sentAt = Date.now();
      var qs = [];
      for (var k in params) {
        if (Object.prototype.hasOwnProperty.call(params, k) && params[k] != null && params[k] !== '') {
          qs.push(encodeURIComponent(k) + '=' + encodeURIComponent(String(params[k])));
        }
      }
      try {
        fetch(journeyUrl + '?' + qs.join('&'), { credentials: 'same-origin', cache: 'no-store', keepalive: true })
          .catch(function () { /* tracing must never affect the page */ });
      } catch (e) { /* ignore */ }
    }

    function bumpCardEpochAndAbortInflight() {
      cardUiEpoch++;
      if (cardFetchAbort) {
        try {
          cardFetchAbort.abort();
        } catch (e) {
          /* ignore */
        }
        cardFetchAbort = null;
      }
    }

    /* The height the slot keeps after a change made while its top was above the screen (see swapSlotContent);
       0 when it keeps none. */
    var keptSlotHeight = 0;
    var keptSlotObserver = null;

    function releaseKeptSlotHeight() {
      if (!keptSlotHeight) return;
      keptSlotHeight = 0;
      slot.style.minHeight = '';
      if (keptSlotObserver) {
        keptSlotObserver.disconnect();
        keptSlotObserver = null;
      }
    }

    /* Lets go of the kept height once the whole slot is below the screen again (the shopper has scrolled back
       above it): a change of size there moves nothing the shopper can see. */
    function watchKeptSlot() {
      if (keptSlotObserver || typeof IntersectionObserver !== 'function') return;
      keptSlotObserver = new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];
        if (!entry.isIntersecting && entry.rootBounds && entry.boundingClientRect.top >= entry.rootBounds.bottom) {
          releaseKeptSlotHeight();
        }
      });
      keptSlotObserver.observe(slot);
    }

    /**
     * Puts new content in the card slot. When the top of the slot is above the screen (the shopper has scrolled
     * past the top of the card, as on Home further down the page), the slot keeps at least the height it had, so
     * a lot ending (the shorter "next presentation" card) does not move the part of the page the shopper is
     * looking at. Where the browser holds the view in place when content above it changes size, it still counts
     * that as the page moving, so the size must not change at all. The kept height goes at the next change while
     * the top of the slot is on screen or below it (which then behaves exactly as before) or once the slot is
     * below the screen again (watchKeptSlot). A taller card still grows the slot, as before.
     */
    function swapSlotContent(html) {
      var box = slot.getBoundingClientRect();
      if (box.height > 0 && box.top < 0) {
        keptSlotHeight = box.height;
        slot.style.minHeight = keptSlotHeight + 'px';
        watchKeptSlot();
      } else {
        releaseKeptSlotHeight();
      }
      slot.innerHTML = html;
    }

    function showEmpty() {
      bumpCardEpochAndAbortInflight();
      clearScheduledRefreshes();
      lastPubNubPriceAt = 0;
      pendingPubNubPrice = null;
      pendingIdentity = { code: '', sku: '' };
      pendingPubNubEmi = null;
      pendingBudgetPay = null;
      pendingBudgetPayIdentity = null;
      budgetPayReconcileCode = '';
      budgetPayReconcileAttempts = 0;
      if (budgetPayReconcileTimer) {
        clearTimeout(budgetPayReconcileTimer);
        budgetPayReconcileTimer = null;
      }
      if (!emptyTemplate || !slot) return;
      var content = emptyTemplate.innerHTML;
      if (content) {
        swapSlotContent(content);
        clearCardIdentity(slot);
      }
    }

    function reinitCardInteractions(scope) {
      if (!scope) return;
      if (typeof window.initCoaCardForms === 'function') {
        window.initCoaCardForms(scope);
      }
      if (typeof window.initViewDetailsPopups === 'function') {
        window.initViewDetailsPopups(scope);
      }
      if (typeof window.BudgetPayLiveTv !== 'undefined' && typeof window.BudgetPayLiveTv.reinit === 'function') {
        window.BudgetPayLiveTv.reinit(scope);
      }
    }

    /**
     * The card and its View Details popup show the same auction, so a price tick has to reach both.
     * Collect from the slot plus the popup for this card wherever it currently lives: openPopup()
     * re-parents it to <body>, which takes it out of the slot subtree for as long as it is open.
     */
    function livePriceScopes(targetSlot) {
      var scopes = [targetSlot];
      var card = targetSlot.querySelector('[data-view-details-popup-id]');
      var popupId = card && card.getAttribute('data-view-details-popup-id');
      if (!popupId) return scopes;
      /* Matched by id over body's direct children rather than getElementById: a refresh that lands
         while a popup is open leaves that id on two nodes, and getElementById would answer with the
         freshly injected hidden one. */
      var detached = document.querySelectorAll('body > .view-details-popup:not([data-ltv-stale])');
      for (var i = 0; i < detached.length; i++) {
        if (detached[i].id === popupId) scopes.push(detached[i]);
      }
      return scopes;
    }

    function setLiveTextInScopes(scopes, selector, text) {
      var found = false;
      for (var s = 0; s < scopes.length; s++) {
        var els = scopes[s].querySelectorAll(selector);
        for (var e = 0; e < els.length; e++) {
          els[e].textContent = text;
          found = true;
        }
      }
      return found;
    }

    function applyPendingPubNubPriceToSlot(targetSlot) {
      if (pendingPubNubPrice == null || !Number.isFinite(pendingPubNubPrice) || !targetSlot) return;
      if (identitiesDiffer(pendingIdentity, readCardIdentity(targetSlot))) return;
      var scopes = livePriceScopes(targetSlot);
      var formatted = formatMoney(pendingPubNubPrice, moneyFormat);
      if (setLiveTextInScopes(scopes, PRICE_SELECTOR, formatted)) {
        targetSlot.setAttribute('data-ltv-live-price', formatted);
      }
      /* Budget Pay measures the shopper's credit against this attribute, and the rendered price
         beside it is money-formatted text it cannot read back. Without this the limit check keeps
         whatever the last HTML render stamped, which for an in-place tick is the whole auction. */
      var liveCents = String(Math.round(pendingPubNubPrice * 100));
      for (var i = 0; i < scopes.length; i++) {
        var toggles = scopes[i].querySelectorAll(BUDGET_PAY_TOGGLE_SELECTOR);
        for (var t = 0; t < toggles.length; t++) {
          toggles[t].setAttribute('data-live-price-cents', liveCents);
        }
      }
      if (pendingPubNubEmi != null && Number.isFinite(pendingPubNubEmi)) {
        setLiveTextInScopes(scopes, INSTALLMENTS_AMOUNT_SELECTOR, formatMoney(pendingPubNubEmi, moneyFormat));
      }
    }

    /**
     * The card and its View Details popup render the same auction from the same snippets, so both
     * carry a copy of the dropdown. openPopup() re-parents the popup to <body>, which takes it out
     * of the slot subtree for as long as it is open.
     */
    function priceScopes() {
      var scopes = [slot];
      var card = slot.querySelector('[data-view-details-popup-id]');
      var popupId = card && card.getAttribute('data-view-details-popup-id');
      if (!popupId) return scopes;
      var detached = document.querySelectorAll('body > .view-details-popup');
      for (var i = 0; i < detached.length; i++) {
        if (detached[i].id === popupId) scopes.push(detached[i]);
      }
      return scopes;
    }

    /** @returns {{key: string, price: number}|null} null when the row carries no usable price */
    function readAuctionLine(item) {
      if (!item || typeof item !== 'object') return null;
      var sku = item.stockCode != null ? item.stockCode : item.StockCode;
      var price = item.currentPrice != null ? item.currentPrice : item.CurrentPrice;
      sku = sku == null ? '' : String(sku).trim();
      var n = Number(price);
      if (!sku || price == null || price === '' || !Number.isFinite(n) || n <= 0) return null;
      return { key: sku.toUpperCase(), price: n };
    }

    /** Price by upper-cased stock code, across GLA rows, their nested MLA rows, and flat MLA rows. */
    function collectLinePrices(data) {
      var map = {};
      if (!data || typeof data !== 'object') return map;
      var lists = [
        data.groupAuctions || data.groupauctions || data.GroupAuctions,
        data.mlaAuctions || data.MLAAuctions,
      ];
      for (var li = 0; li < lists.length; li++) {
        var list = lists[li];
        if (!Array.isArray(list)) continue;
        for (var i = 0; i < list.length; i++) {
          var line = readAuctionLine(list[i]);
          if (line) map[line.key] = line.price;
          var nested = list[i] && (list[i].mlauctions || list[i].mlaAuctions || list[i].MLAAuctions);
          if (!Array.isArray(nested)) continue;
          for (var j = 0; j < nested.length; j++) {
            var nestedLine = readAuctionLine(nested[j]);
            if (nestedLine) map[nestedLine.key] = nestedLine.price;
          }
        }
      }
      /* The main lot is never one of the GLA/MLA rows, so nothing refreshed the option carrying its
         own sku — it kept the data-line-price the server stamped at page load, and
         repaintSelectedLinePrice() wrote that page-load figure back over the live price. A genuine
         row for the same sku is the narrower price and still wins, hence the hasOwnProperty guard.
         Parsed rather than type-checked: AMS sends CurrentPrice as a string on the live channel. */
      var mainKey = extractSku(data).toUpperCase();
      if (mainKey !== '' && !Object.prototype.hasOwnProperty.call(map, mainKey)) {
        var mainNode = data.MainAuctions || data.mainAuctions || {};
        var mainRaw = data.currentPrice != null
          ? data.currentPrice
          : (mainNode.CurrentPrice != null ? mainNode.CurrentPrice : mainNode.currentPrice);
        var mainNum = Number(mainRaw);
        if (mainRaw != null && mainRaw !== '' && Number.isFinite(mainNum) && mainNum > 0) {
          map[mainKey] = mainNum;
        }
      }
      return map;
    }

    /** Buy All renders its price inside the option text, so the label has to be rebuilt from its template. */
    function applyBuyAllLinePrice(opt, price) {
      var template = opt.getAttribute('data-buy-all-label');
      if (template && template.indexOf('__PRICE__') !== -1) {
        opt.textContent = template.replace('__PRICE__', formatMoney(price, moneyFormat));
      }
      var form = opt.closest('form');
      if (!form) return;
      var row = form.querySelector('[data-buy-all-skus]');
      if (row) row.setAttribute('data-buy-all-price', String(price));
      /* Carried into the cart as a line property, so a stale value outlives the page. */
      var priceInput = form.querySelector('input[name="properties[_buyAllPrice]"]');
      if (priceInput) priceInput.value = String(price);
    }

    /**
     * Later selects are the narrower choice (GLA → MLA), so the last one with a price wins.
     *
     * Only options stamped LINE_PRICE_LIVE_ATTR count. data-line-price is written by the server at
     * page load and by the MLA rebuilds in live-tv-card-forms.js from page-load config, so on its
     * own it proves nothing about the current price. This repaint exists to restore the selected
     * line's price after applyPendingPubNubPriceToSlot() stamps the main lot's — but reading an
     * option no tick had ever priced restored the page-load figure instead, and since the poll
     * only repairs the price while it is still moving, that stale figure survived every later tick
     * until the shopper reloaded. An option priced by an earlier tick still qualifies: its value is
     * the last one AMS actually reported for that line.
     */
    function repaintSelectedLinePrice(scope) {
      var priceEl = scope.querySelector('.currently-on-air-card__price, .view-details-popup__price');
      if (!priceEl) return;
      var selects = scope.querySelectorAll('select[data-gla-select], select[data-mla-select], select[data-variant-select]');
      var price = null;
      for (var i = 0; i < selects.length; i++) {
        var opt = selects[i].options[selects[i].selectedIndex];
        if (!opt || !opt.getAttribute || !opt.hasAttribute(LINE_PRICE_LIVE_ATTR)) continue;
        var raw = opt.getAttribute('data-line-price');
        if (raw == null || String(raw).trim() === '') continue;
        var n = Number(raw);
        if (Number.isFinite(n) && n > 0) price = n;
      }
      if (price != null) priceEl.textContent = formatMoney(price, moneyFormat);
    }

    /**
     * Refresh the per-line prices stamped on the rendered options. A group line can be re-priced
     * while the main lot's price stays put, and an in-place tick never re-renders the card, so
     * without this the options (and the Buy All label) keep their render-time prices for the whole lot.
     */
    function applyLinePrices(data) {
      var map = collectLinePrices(data);
      if (Object.keys(map).length === 0) return;
      var scopes = priceScopes();
      for (var s = 0; s < scopes.length; s++) {
        var opts = scopes[s].querySelectorAll('option[data-sku], option[data-stock-code], option[data-buy-all]');
        for (var o = 0; o < opts.length; o++) {
          var opt = opts[o];
          var isBuyAll = opt.hasAttribute('data-buy-all');
          var key = isBuyAll
            ? 'BUYALL'
            : String(opt.getAttribute('data-sku') || opt.getAttribute('data-stock-code') || '').trim().toUpperCase();
          if (!key || !Object.prototype.hasOwnProperty.call(map, key)) continue;
          var price = map[key];
          /* Before the unchanged-price bail below: a tick that repeats the same figure has still
             confirmed it, which is exactly what repaintSelectedLinePrice() needs to know. */
          opt.setAttribute(LINE_PRICE_LIVE_ATTR, '');
          if (Number(opt.getAttribute('data-line-price')) === price) continue;
          opt.setAttribute('data-line-price', String(price));
          if (isBuyAll) applyBuyAllLinePrice(opt, price);
        }
        repaintSelectedLinePrice(scopes[s]);
      }
    }

    function clearBudgetPayUiInScope(targetSlot) {
      var wraps = targetSlot.querySelectorAll('[data-ltv-budget-pay]');
      for (var i = 0; i < wraps.length; i++) {
        wraps[i].setAttribute('hidden', '');
        wraps[i].style.display = 'none';
      }
      var forms = targetSlot.querySelectorAll('form');
      for (var f = 0; f < forms.length; f++) {
        var sp = forms[f].querySelector('input[name="selling_plan"]');
        if (sp && sp.parentNode) sp.parentNode.removeChild(sp);
        var toggles = forms[f].querySelectorAll('[data-budget-pay-toggle]');
        for (var t = 0; t < toggles.length; t++) {
          toggles[t].removeAttribute('data-active');
          var btn = toggles[t].querySelector('button');
          if (btn) btn.setAttribute('aria-pressed', 'false');
        }
      }
    }

    /**
     * @param {boolean} allowReconcile — only the post-swap path may schedule a reconcile refresh.
     *   Called from an in-place price tick it would refresh (and reset the card) roughly a second
     *   after every PubNub message on any BP auction whose markup has no [data-ltv-budget-pay].
     */
    function applyPendingBudgetPayToSlot(targetSlot, epochForThisFetch, allowReconcile) {
      if (!targetSlot) return;
      if (identitiesDiffer(pendingBudgetPayIdentity, readCardIdentity(targetSlot))) return;
      if (!pendingBudgetPay) return;
      var wraps = targetSlot.querySelectorAll('[data-ltv-budget-pay]');
      if (!pendingBudgetPay.enabled) {
        clearBudgetPayUiInScope(targetSlot);
        return;
      }
      for (var j = 0; j < wraps.length; j++) {
        wraps[j].removeAttribute('hidden');
        wraps[j].style.display = '';
      }
      if (pendingBudgetPay.emiPrice != null && Number.isFinite(pendingBudgetPay.emiPrice)) {
        setLiveTextInScopes(
          livePriceScopes(targetSlot),
          INSTALLMENTS_AMOUNT_SELECTOR,
          formatMoney(pendingBudgetPay.emiPrice, moneyFormat)
        );
      }
      if (allowReconcile && pendingBudgetPay.enabled && wraps.length === 0 && epochForThisFetch === cardUiEpoch) {
        scheduleBudgetPayReconcile(epochForThisFetch);
      }
    }

    function scheduleBudgetPayReconcile(epochForThisFetch) {
      /* Attempts are capped per auction, not per message: the counter used to reset on every
         inbound message, so a BP auction whose card never renders [data-ltv-budget-pay] kept
         refreshing (and resetting the shopper's selection) for as long as it stayed on air. */
      var codeForReconcile = identityKey(pendingIdentity);
      if (codeForReconcile !== budgetPayReconcileCode) {
        budgetPayReconcileCode = codeForReconcile;
        budgetPayReconcileAttempts = 0;
      }
      if (budgetPayReconcileAttempts >= BUDGET_PAY_RECONCILE_MAX_ATTEMPTS) return;
      budgetPayReconcileAttempts++;
      if (budgetPayReconcileTimer) clearTimeout(budgetPayReconcileTimer);
      budgetPayReconcileTimer = setTimeout(function() {
        budgetPayReconcileTimer = null;
        if (epochForThisFetch !== cardUiEpoch) return;
        if (!pendingBudgetPay || !pendingBudgetPay.enabled) return;
        if (cardHasShopperInput(slot)) {
          coaDebugLog('budgetPayReconcile skipped (shopper input)');
          return;
        }
        fetch(apiUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function(r) { return r.json(); })
          .then(function(res) {
            if (epochForThisFetch !== cardUiEpoch) return;
            var bp = res && res.data && res.data.budgetPay;
            var apiOn = bp && (bp.enabled === true || bp.isBPAuction === true);
            if (apiOn) {
              refreshCard('budget-pay-reconcile');
            } else {
              scheduleBudgetPayReconcile(epochForThisFetch);
            }
          })
          .catch(function() {
            if (epochForThisFetch === cardUiEpoch) refreshCard('budget-pay-reconcile-error');
          });
      }, 1100);
    }

    /** True once the card on screen is the lot `target` names (an identity-less target: any lot). */
    function cardShowsTarget(target) {
      var card = readCardIdentity(slot);
      return hasIdentity(card) && !identitiesDiffer(target, card);
    }

    function clearScheduledRefreshes() {
      if (newAuctionRefreshTimer) {
        clearTimeout(newAuctionRefreshTimer);
        newAuctionRefreshTimer = null;
      }
      newAuctionRefreshKey = '';
      if (missRetryTimer) {
        clearTimeout(missRetryTimer);
        missRetryTimer = null;
      }
    }

    /**
     * A PubNub message naming a lot the card does not show. The first one for that lot schedules a
     * single refresh after NEW_AUCTION_REFRESH_DELAY_MS (+ jitter); repeats while it is pending are
     * dropped. Once a refresh toward the lot has run, later messages go straight to refreshCard()
     * so its cooldown and cap apply exactly as before.
     */
    function scheduleNewAuctionRefresh(trigger, target) {
      var key = identityKey(target);
      if (key === lastRefreshTarget) {
        refreshCard(trigger, target);
        return;
      }
      if (newAuctionRefreshTimer) {
        if (key === newAuctionRefreshKey) return;
        clearTimeout(newAuctionRefreshTimer);
      }
      newAuctionRefreshKey = key;
      var delay = NEW_AUCTION_REFRESH_DELAY_MS + Math.floor(Math.random() * NEW_AUCTION_REFRESH_JITTER_MS);
      coaDebugLog('refreshCard scheduled', { trigger: trigger, target: key, delayMs: delay });
      journeyNoteScheduled(target, delay);
      newAuctionRefreshTimer = setTimeout(function () {
        newAuctionRefreshTimer = null;
        newAuctionRefreshKey = '';
        /* The poll may have brought the lot in while this waited. */
        if (cardShowsTarget(target)) {
          coaDebugLog('scheduled refresh skipped, card already shows target', { target: key });
          return;
        }
        refreshCard(trigger, target);
      }, delay);
    }

    /** A targeted refresh rendered another lot: retry once, NEW_AUCTION_RETRY_DELAY_MS later. */
    function scheduleMissRetry(trigger, target) {
      var key = identityKey(target);
      if (missRetryUsedFor === key) return;
      missRetryUsedFor = key;
      if (missRetryTimer) clearTimeout(missRetryTimer);
      coaDebugLog('refreshCard missed target, retry scheduled', {
        trigger: trigger,
        target: key,
        delayMs: NEW_AUCTION_RETRY_DELAY_MS
      });
      missRetryTimer = setTimeout(function () {
        missRetryTimer = null;
        if (cardShowsTarget(target)) return;
        refreshCard((trigger || 'refresh') + '-retry', target, true);
      }, NEW_AUCTION_RETRY_DELAY_MS);
    }

    /**
     * @param {{code: string, sku: string}} [target] — the identity this refresh is trying to reach.
     *   A refresh can legitimately fail to reach it: PubNub announces a new auction before the Kafka
     *   consumer has written the shop metafield, so the page still renders the previous lot (or the
     *   empty state). Retries toward an unreached target are therefore throttled and then capped,
     *   rather than either giving up after one try or re-fetching the page on every poll forever.
     * @param {boolean} [skipCooldown] — for scheduleMissRetry() and the poll: skips the cooldown, never the cap.
     */
    function refreshCard(trigger, target, skipCooldown) {
      if (!slot) return;
      var targetKey = target ? identityKey(target) : '';
      var now = Date.now();
      if (targetKey !== '') {
        var sinceLast = now - lastRefreshAt;
        if (targetKey !== lastRefreshTarget) {
          lastRefreshTarget = targetKey;
          sameTargetRefreshes = 0;
          missRetryUsedFor = '';
        } else if (!skipCooldown && sinceLast < REFRESH_RETARGET_COOLDOWN_MS) {
          coaDebugLog('refreshCard throttled', { trigger: trigger, target: targetKey });
          return;
        } else if (sinceLast < REFRESH_SAME_TARGET_MIN_GAP_MS) {
          coaDebugLog('refreshCard skipped, same lot requested moments ago', { trigger: trigger, target: targetKey });
          return;
        } else if (sameTargetRefreshes >= MAX_REFRESHES_PER_TARGET) {
          if (sinceLast < REFRESH_TARGET_RETRY_AFTER_MS) {
            coaDebugLog('refreshCard capped', { trigger: trigger, target: targetKey, attempts: sameTargetRefreshes });
            return;
          }
          sameTargetRefreshes = 0;
          missRetryUsedFor = '';
        }
        sameTargetRefreshes++;
        lastRefreshAt = now;
        journeyNoteFetch(target, trigger);
        /* This fetch supersedes anything still waiting to reach the same lot. */
        if (newAuctionRefreshTimer && newAuctionRefreshKey === targetKey) {
          clearTimeout(newAuctionRefreshTimer);
          newAuctionRefreshTimer = null;
          newAuctionRefreshKey = '';
        }
        if (missRetryTimer) {
          clearTimeout(missRetryTimer);
          missRetryTimer = null;
        }
      }
      var snapshot = snapshotCardSelection(slot);
      coaDebugLog('refreshCard', {
        trigger: trigger || 'unknown',
        target: targetKey,
        cardIdentity: identityKey(readCardIdentity(slot)),
        hasSnapshot: !!snapshot
      });
      bumpCardEpochAndAbortInflight();
      if (budgetPayReconcileTimer) {
        clearTimeout(budgetPayReconcileTimer);
        budgetPayReconcileTimer = null;
      }
      var epochForThisFetch = cardUiEpoch;
      cardFetchAbort = new AbortController();
      var signal = cardFetchAbort.signal;
      var base = (blockRoot.getAttribute('data-section-render-base') || window.location.pathname).trim() || window.location.pathname;
      var pathPart = base.indexOf('?') !== -1 ? base.slice(0, base.indexOf('?')) : base;
      var path = pathPart.charAt(0) === '/' ? pathPart : '/' + pathPart;
      /* Unique per request: the storefront page is edge-cached, and a cached copy rendered before
         the metafield write would keep handing back the previous lot no matter how often we retry. */
      var cacheBust = '_ltv=' + now;
      var fullPageUrl = window.location.origin + path + '?' + cacheBust;
      /* Shopify's Section Rendering API returns just this section's markup instead of the whole
         document. The Live TV page is ~906 KB, and every auction change plus every 10 s poll was
         downloading and DOMParser-ing all of it to lift one card out — the dominant contributor
         to the 23.5 s "Style & Layout" bucket on this page. The section fragment is a fraction of
         that. If the theme or the section id ever stops answering in that form, the response
         simply will not contain the card column and we retry the full page, so the worst case is
         today's behaviour plus one small request. */
      var sectionId = (blockRoot.getAttribute('data-section-id') || '').trim();
      var firstUrl = sectionId
        ? window.location.origin + path + '?section_id=' + encodeURIComponent(sectionId) + '&' + cacheBust
        : fullPageUrl;
      fetchPageHtmlAndExtractCard(
        firstUrl, slot, epochForThisFetch, signal, snapshot,
        firstUrl === fullPageUrl ? null : fullPageUrl,
        trigger, targetKey !== '' ? target : null
      );
    }

    /**
     * @param {string} [trigger] — what asked for this refresh (debug log / retry label).
     * @param {{code: string, sku: string}|null} [refreshTarget] — set for targeted refreshes; a render
     *   of a different lot then schedules scheduleMissRetry(). Failed requests do not: they wait for
     *   the next trigger as before, so an erroring storefront is never re-hit on a timer.
     */
    function fetchPageHtmlAndExtractCard(pageUrl, targetSlot, epochForThisFetch, signal, snapshot, fallbackUrl, trigger, refreshTarget) {
      fetch(pageUrl, { credentials: 'same-origin', headers: { 'Accept': 'text/html' }, signal: signal })
        .then(function(res) {
          if (epochForThisFetch !== cardUiEpoch) return null;
          if (!res.ok) return null;
          return res.text();
        })
        .then(function(text) {
          if (epochForThisFetch !== cardUiEpoch) return;
          if (refreshTarget) journeyNoteFetched(refreshTarget);
          if (!text || text.length < 100) {
            if (fallbackUrl) fetchPageHtmlAndExtractCard(fallbackUrl, targetSlot, epochForThisFetch, signal, snapshot, null, trigger, refreshTarget);
            return;
          }
          var parser = new DOMParser();
          var doc = parser.parseFromString(text, 'text/html');
          var cardCol = doc.querySelector(CARD_COLUMN_SELECTOR) || doc.querySelector(SECTION_SLOT_SELECTOR);
          if (!cardCol && fallbackUrl) {
            coaDebugLog('section render miss, retrying full page', { url: pageUrl });
            fetchPageHtmlAndExtractCard(fallbackUrl, targetSlot, epochForThisFetch, signal, snapshot, null, trigger, refreshTarget);
            return;
          }
          if (cardCol && cardCol.innerHTML) {
            /* The page still renders the ended lot until the metafield is cleared — keep the empty state. */
            if (isEndedAuction(stampCardIdentity(cardCol))) {
              coaDebugLog('refreshCard dropped ended auction', { url: pageUrl });
              if (epochForThisFetch === cardUiEpoch) cardFetchAbort = null;
              return;
            }
            /* Remove any view-details popups previously moved to body so duplicate IDs don't confuse getElementById after reinject. Skip open popups so a card refresh during user interaction doesn't yank the popup out from under them. */
            document.querySelectorAll('body > .view-details-popup').forEach(function (p) {
              if (!p.classList.contains('is-open')) p.remove();
              /* Kept on screen, but it now shows a lot that is no longer on air: the popup id is
                 per-block, so without this flag the next price tick would match it by id and stamp
                 the new lot's price onto the old lot's content. */
              else p.setAttribute('data-ltv-stale', '');
            });
            /* targetSlot is this block's slot (refreshCard passes it), so the swap goes through swapSlotContent. */
            swapSlotContent(cardCol.innerHTML);
            var newIdentity = identityKey(stampCardIdentity(targetSlot));
            if (refreshTarget && !cardShowsTarget(refreshTarget)) {
              scheduleMissRetry(trigger, refreshTarget);
            }
            journeyNoteDisplayed();
            applyPendingPubNubPriceToSlot(targetSlot);
            applyPendingBudgetPayToSlot(targetSlot, epochForThisFetch, true);
            setTimeout(function () {
              if (epochForThisFetch !== cardUiEpoch) return;
              reinitCardInteractions(targetSlot);
              /* Only replay onto the same auction — a new lot must start from a clean card. */
              if (snapshot && snapshot.identity !== '|' && snapshot.identity === newIdentity) {
                restoreCardSelection(targetSlot, snapshot, 0, function () {
                  return epochForThisFetch !== cardUiEpoch;
                });
              }
            }, 0);
            if (epochForThisFetch === cardUiEpoch) cardFetchAbort = null;
          }
        })
        .catch(function(err) {
          if (err && err.name === 'AbortError') return;
          if (fallbackUrl) fetchPageHtmlAndExtractCard(fallbackUrl, targetSlot, epochForThisFetch, signal, snapshot, null, trigger, refreshTarget);
        });
    }

    /**
     * Budget Pay state for a poll payload, always derived from that payload's own price.
     * The caller binds it to the auction via pendingBudgetPayIdentity.
     * @returns {{enabled: boolean, emiCount: number, emiPrice: number|null}|null} null = payload carries no Budget Pay
     */
    function budgetPayFromPoll(data, currentPrice) {
      var bp = data && data.budgetPay;
      if (!bp || typeof bp !== 'object') return null;
      var count = Number(bp.emiCount) || 0;
      return {
        enabled: bp.enabled === true || bp.isBPAuction === true,
        emiCount: count,
        emiPrice: count >= 1 && currentPrice != null ? currentPrice / count : null
      };
    }

    function onPollResponse(res) {
      if (!res || res.success !== true) return;
      var isLive = res.isLive === true;
      var pollIdentity = messageIdentity(res.data);

      if (isLive === false) {
        notLivePolls++;
        if (lastIsLive !== false && notLivePolls >= NOT_LIVE_POLLS_BEFORE_EMPTY) {
          coaDebugLog('showEmpty (poll)', { notLivePolls: notLivePolls });
          showEmpty();
          lastIsLive = false;
        }
        return;
      }
      if (isEndedAuction(pollIdentity)) {
        coaDebugLog('poll ignored, it still reports the lot PubNub ended', { target: identityKey(pollIdentity) });
        return;
      }
      notLivePolls = 0;

      var currentPrice = (res.data && res.data.currentPrice != null && Number.isFinite(Number(res.data.currentPrice)))
        ? Number(res.data.currentPrice) : null;

      /* Compared against the card actually on screen, not against what PubNub last told us — that
         is what makes the two sources agree instead of each undoing the other's conclusion. */
      var cardIdentity = readCardIdentity(slot);
      /* An unidentified card while the API reports a live auction means the card is the empty state
         (or a render that never resolved a product). identitiesDiffer() reads that as "no
         contradiction", so without this the poll would go quiet and leave "Next presentation will
         start shortly" on screen for the rest of the auction — reachable only by a manual reload. */
      var cardMissingLiveAuction = hasIdentity(pollIdentity) && !hasIdentity(cardIdentity);
      var auctionChanged = identitiesDiffer(pollIdentity, cardIdentity) || cardMissingLiveAuction;

      if (lastIsLive !== true || auctionChanged) {
        if (auctionChanged) journeyNoteSeen('poll', pollIdentity, res.data);
        pendingIdentity = pollIdentity;
        if (currentPrice != null) {
          pendingPubNubPrice = currentPrice;
          var bpEmi = res.data.budgetPay;
          pendingPubNubEmi =
            bpEmi && bpEmi.enabled && Number(bpEmi.emiCount) > 0
              ? currentPrice / Number(bpEmi.emiCount)
              : null;
        }
        var bpPoll = budgetPayFromPoll(res.data, currentPrice);
        if (bpPoll) {
          pendingBudgetPay = bpPoll;
          pendingBudgetPayIdentity = pollIdentity;
        }
        /* skipCooldown: the poll reports the metafield itself, so it can only ask once per
           POLL_INTERVAL_MS and the lot it names is already renderable — the cooldown exists for PubNub
           bursts. Without this, a timed retry a few seconds earlier would block the poll that finally
           sees the lot. MAX_REFRESHES_PER_TARGET still applies. */
        refreshCard(
          lastIsLive !== true ? 'poll-went-live' : (cardMissingLiveAuction ? 'poll-card-empty' : 'poll-auction-changed'),
          pollIdentity,
          true
        );
        lastIsLive = true;
        lastPrice = currentPrice;
      } else {
        /* Unconditional: a group line can be re-priced while the main lot's price is unchanged. */
        applyLinePrices(res.data);
        /* Keeps the sold badge moving when PubNub is down (the poll returns the metafield's soldSoFar). */
        applySoldCount(slot, null, res.data && res.data.soldSoFar != null ? Number(res.data.soldSoFar) : null);
        /* Also unconditional, and for the same reason the guard above exists: this is the value
           every later tick stamps on the card, and while an auction sits at one price nothing else
           refreshes it. The poll is the one source that is always about the card on screen, so it
           is what heals a figure that arrived from somewhere else — within one POLL_INTERVAL_MS
           rather than at the end of the auction. */
        var bpSame = budgetPayFromPoll(res.data, currentPrice);
        if (bpSame) {
          pendingBudgetPay = bpSame;
          pendingBudgetPayIdentity = pollIdentity;
        }
        if (currentPrice != null && currentPrice !== lastPrice) {
          if (Date.now() - lastPubNubPriceAt < PUBNUB_PRICE_FRESH_MS) {
            /* lastPrice is left as it was, so a later poll still heals the figure if PubNub goes quiet. */
            coaDebugLog('poll price skipped, a newer PubNub price is on the card', { pollPrice: currentPrice });
          } else {
            pendingIdentity = pollIdentity;
            pendingPubNubPrice = currentPrice;
            if (pendingBudgetPay && pendingBudgetPay.enabled && pendingBudgetPay.emiCount > 0) {
              pendingPubNubEmi = currentPrice / pendingBudgetPay.emiCount;
            }
            applyPendingPubNubPriceToSlot(slot);
            lastPrice = currentPrice;
          }
        }
      }
    }

    /* A failed poll (dropped connection, proxy 5xx returning theme HTML, success !== true) changes
       nothing on screen: the card keeps the last good product. It only slows the next polls down,
       one extra tick per consecutive failure up to POLL_MAX_BACKOFF_TICKS, and resets on success.
       (TWS-1394 — first shipped in the app theme extension, lost when Live TV moved to this repo.) */
    var pollFailures = 0;
    var pollTicksToSkip = 0;

    function pollFailed() {
      pollFailures++;
      pollTicksToSkip = Math.min(pollFailures, POLL_MAX_BACKOFF_TICKS);
    }

    function poll() {
      /* cache: 'no-cache' — the API used to send max-age=5 + stale-while-revalidate=10, so with a 10 s
         interval the browser answered each poll with the previous poll's response. Every poll already
         went to the network (revalidation), so this adds no requests; it only uses the fresh answer. */
      fetch(apiUrl, { credentials: 'same-origin', cache: 'no-cache', headers: { 'Accept': 'application/json', 'ngrok-skip-browser-warning': 'true' } })
        .then(function(res) { return res.json(); })
        .then(function(data) {
          if (!data || data.success !== true) { pollFailed(); return; }
          pollFailures = 0;
          pollTicksToSkip = 0;
          onPollResponse(data);
        })
        .catch(pollFailed);
    }

    function pollTick() {
      /* A background tab has nothing to repaint and PubNub still delivers live changes; the
         visibilitychange handler below polls straight away when the shopper comes back. */
      if (document.hidden) return;
      if (pollTicksToSkip > 0) { pollTicksToSkip--; return; }
      poll();
    }

    poll();
    setInterval(pollTick, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) poll();
    });

    /* pubnub.min.js is 221 KB and its only job is live price/auction-change messages, none of
       which matter before the page has painted. The block no longer ships a <script> tag for it;
       it is fetched on idle instead and this init runs once it lands. poll() above already ran,
       so the card is correct from first load regardless, and a late subscribe costs at most one
       POLL_INTERVAL_MS of latency on the first tick. */
    function startPubNub() {
      if (!subscribeKey || typeof window.PubNub !== 'function') return;
      try {
        var pubnub = new window.PubNub({
          subscribeKey: subscribeKey,
          userId: 'live-tv-viewer-' + Date.now() + '-' + Math.random().toString(36).substring(2, 10)
        });
        pubnub.addListener({
          message: function(ev) {
            var ch = ev.channel;
            if (ch === channelAuctionEnded) {
              /* An end message that does not name its lot ends the one on the card (read before showEmpty clears it). */
              var endedId = messageIdentity(ev.message);
              if (!hasIdentity(endedId)) endedId = readCardIdentity(slot);
              if (hasIdentity(endedId)) {
                endedLotIdentity = endedId;
                endedLotAt = Date.now();
              }
              showEmpty();
              lastIsLive = false;
              notLivePolls = NOT_LIVE_POLLS_BEFORE_EMPTY;
              /* ltv-app.js reloads Last 24 Items / Last 24 Hours on this. */
              document.dispatchEvent(new CustomEvent('ltv:auction-ended'));
            } else if (ch === channelLivetv) {
              var msg = ev.message;
              /* A late tick for the lot that just ended (or one with no identity while it may still be
                 that lot) must not refresh the empty card back to it. A real new lot is picked up by
                 its own identified messages or by the poll. */
              var earlyIdentity = messageIdentity(msg);
              if (isEndedAuction(earlyIdentity) || (endedRecently() && !hasIdentity(earlyIdentity) && !hasIdentity(readCardIdentity(slot)))) {
                coaDebugLog('pubnub ignored ended auction', { identity: identityKey(earlyIdentity) });
                return;
              }
              var lp = extractLivePrice(msg);
              if (lp != null) pendingPubNubPrice = lp;
              var msgIdentity = messageIdentity(msg);
              if (hasIdentity(msgIdentity)) pendingIdentity = msgIdentity;
              /* Fall back to pendingPubNubPrice only when this message is not about some other
                 auction: it is a single slot shared by every message, so after a foreign one it
                 holds a price that has nothing to do with the card on screen. */
              var bpBasis = lp;
              if (bpBasis == null && !identitiesDiffer(msgIdentity, readCardIdentity(slot))) {
                bpBasis = pendingPubNubPrice;
              }
              var bpSt = extractBudgetPayState(msg, bpBasis);
              if (bpSt != null) {
                pendingBudgetPay = bpSt;
                /* An identity-less message cannot be attributed to another lot, so it belongs to
                   whatever is on screen; anything else is bound to its own auction. */
                pendingBudgetPayIdentity = hasIdentity(msgIdentity) ? msgIdentity : readCardIdentity(slot);
                var emi = extractBudgetPayInstallment(msg, bpBasis);
                pendingPubNubEmi = emi;
              } else {
                var emiOnly = extractBudgetPayInstallment(msg, bpBasis);
                if (emiOnly != null) pendingPubNubEmi = emiOnly;
              }
              /* Same auction still live (e.g. a bid just landed and this is only a price tick):
                 patch price/installments/budget-pay in place instead of a full refreshCard().
                 refreshCard() replaces the card's entire innerHTML from a fresh page fetch, which
                 both flickers visibly and drops the shopper back to an unselected card — so it must
                 happen only when this message actually contradicts the card on screen. A payload
                 with no identity fields proves nothing and is treated as a tick; if it really was a
                 new auction, the poll carries the full record and picks it up within POLL_INTERVAL_MS. */
              var liveCardIdentity = readCardIdentity(slot);
              var cardIsEmpty = !hasIdentity(liveCardIdentity);
              if (lastIsLive === true && !cardIsEmpty && !identitiesDiffer(msgIdentity, liveCardIdentity)) {
                if (lp != null) lastPubNubPriceAt = Date.now();
                applyPendingPubNubPriceToSlot(slot);
                applyLinePrices(msg);
                applyPendingBudgetPayToSlot(slot, cardUiEpoch, false);
                applySoldCount(slot, extractStockRemaining(msg), null);
              } else {
                /* Not refreshed on the spot: this message usually beats the metafield write the page
                   renders from (see NEW_AUCTION_REFRESH_DELAY_MS). */
                journeyNoteSeen('pubnub', msgIdentity, msg);
                scheduleNewAuctionRefresh(cardIsEmpty ? 'pubnub-card-empty' : 'pubnub-auction-changed', msgIdentity);
                lastIsLive = true;
                notLivePolls = 0;
              }
            }
          }
        });
        pubnub.subscribe({ channels: [channelLivetv, channelAuctionEnded] });
      } catch (e) { /* PubNub unavailable, polling already started above */ }
    }

    if (subscribeKey) loadPubNubThenStart(blockRoot, startPubNub);
  }

  /**
   * Fetch pubnub.min.js once per page, on idle, and run every waiting starter.
   * Several blocks can ask for it; only the first injects the tag.
   */
  var pubNubLoadState = null;
  var pubNubWaiters = [];

  function loadPubNubThenStart(blockRoot, starter) {
    if (typeof window.PubNub === 'function') { starter(); return; }
    pubNubWaiters.push(starter);
    if (pubNubLoadState) return;
    var src = (blockRoot.getAttribute('data-pubnub-src') || '').trim();
    if (!src) { pubNubLoadState = 'unavailable'; return; }
    pubNubLoadState = 'loading';
    var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 200); };
    idle(function () {
      var tag = document.createElement('script');
      tag.src = src;
      tag.async = true;
      tag.onload = function () {
        pubNubLoadState = 'loaded';
        var waiting = pubNubWaiters;
        pubNubWaiters = [];
        for (var i = 0; i < waiting.length; i++) {
          try { waiting[i](); } catch (e) { /* one block failing must not stop the others */ }
        }
      };
      tag.onerror = function () { pubNubLoadState = 'error'; pubNubWaiters = []; };
      document.head.appendChild(tag);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
